'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { WORDMARK_PATH, LOCKUP_WIDTH, WORDMARK_OFFSET_X, SYMBOL_TRANSFORM, COMPACT_SYMBOL_TRANSFORM, LOCKUP_SYMBOL_TRANSFORM, CENTERED_ORBIT_EYES, roundedEyeAttributes } from '@/lib/brand-artwork.mjs';
import styles from './AnimatedBrandSymbol.module.css';

export const BRAND_EYE_EXPRESSIONS = Object.freeze({
  BLINK: 'blink',
  FOCUS: 'focus',
  JOY: 'joy',
  GAZE: 'gaze',
  REST: 'rest',
  SCAN: 'scan',
  WINK: 'wink',
  HORIZON: 'horizon',
});

const EXPRESSION_DURATIONS = Object.freeze({
  blink: 1450,
  focus: 1650,
  joy: 1850,
  gaze: 2050,
  rest: 2250,
  scan: 1900,
  wink: 2100,
  horizon: 2700,
});

const VALID_EXPRESSIONS = new Set(Object.values(BRAND_EYE_EXPRESSIONS));
const IDLE_EXPRESSIONS = Object.freeze(Object.values(BRAND_EYE_EXPRESSIONS));
const DEFAULT_COLOR = '#FFFFFF';

function createIdleExpressionQueue(previousExpression) {
  const queue = [...IDLE_EXPRESSIONS];

  for (let index = queue.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [queue[index], queue[swapIndex]] = [queue[swapIndex], queue[index]];
  }

  if (queue[0] === previousExpression) {
    const swapIndex = 1 + Math.floor(Math.random() * (queue.length - 1));
    [queue[0], queue[swapIndex]] = [queue[swapIndex], queue[0]];
  }

  return queue;
}

function CapsuleEye({ cx, cy = CENTERED_ORBIT_EYES.y, width = CENTERED_ORBIT_EYES.width, height = CENTERED_ORBIT_EYES.height, angle = 0 }) {
  return (
    <g className={styles.eye}>
      <rect {...roundedEyeAttributes({ cx, cy, width, height })} transform={`rotate(${angle} ${cx} ${cy})`} />
    </g>
  );
}

function SmileEye({ cx }) {
  return (
    <g className={styles.eye}>
      <path d={`M${cx - 8.5} 55Q${cx} 41.8 ${cx + 8.5} 55`} fill="none" stroke="#000" strokeWidth="7.2" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );
}

function BaseEyes() {
  return <g className={styles.baseEyes} fill="#000"><CapsuleEye cx={CENTERED_ORBIT_EYES.left} /><CapsuleEye cx={CENTERED_ORBIT_EYES.right} /></g>;
}

function AlternateEyes({ expression }) {
  if (expression === BRAND_EYE_EXPRESSIONS.WINK) {
    return (
      <>
        <g className={styles.winkLeft} fill="#000"><SmileEye cx={35} /><CapsuleEye cx={65} /></g>
        <g className={styles.winkRight} fill="#000"><CapsuleEye cx={35} /><SmileEye cx={65} /></g>
      </>
    );
  }
  if (expression === BRAND_EYE_EXPRESSIONS.HORIZON) {
    return (
      <g className={styles.horizonEyes} fill="#000">
        <g className={styles.horizonEye}><rect {...roundedEyeAttributes({ cx: 0, cy: 0 })} /></g>
        <g className={styles.horizonEye}><rect {...roundedEyeAttributes({ cx: 0, cy: 0 })} /></g>
      </g>
    );
  }
  if (expression === BRAND_EYE_EXPRESSIONS.FOCUS) {
    return <g className={styles.altEyes} fill="#000"><g className={styles.eye}><circle cx="35" cy="50" r="8.2" /></g><g className={styles.eye}><circle cx="65" cy="50" r="8.2" /></g></g>;
  }
  if (expression === BRAND_EYE_EXPRESSIONS.JOY) {
    return <g className={styles.altEyes}><SmileEye cx={35} /><SmileEye cx={65} /></g>;
  }
  if (expression === BRAND_EYE_EXPRESSIONS.GAZE) {
    return <g className={styles.altEyes} fill="#000"><CapsuleEye cx={35} width={10.5} height={25} angle={16} /><CapsuleEye cx={65} width={10.5} height={25} angle={-16} /></g>;
  }
  if (expression === BRAND_EYE_EXPRESSIONS.REST) {
    return <g className={styles.altEyes} fill="#000"><CapsuleEye cx={35} width={15} height={5} /><CapsuleEye cx={65} width={15} height={5} /></g>;
  }
  if (expression === BRAND_EYE_EXPRESSIONS.SCAN) {
    return <g className={styles.altEyes} fill="#000"><CapsuleEye cx={35} width={5.6} /><CapsuleEye cx={65} width={5.6} /></g>;
  }
  return null;
}

export default function AnimatedBrandSymbol({
  alt = 'BotGo',
  animated = true,
  compact = false,
  lockup = false,
  className = '',
  color = DEFAULT_COLOR,
  decorative = true,
  enableIdleMotion = true,
  expression = null,
  hoverExpression = BRAND_EYE_EXPRESSIONS.FOCUS,
  size = 40,
}) {
  const rootRef = useRef(null);
  const endTimerRef = useRef(null);
  const idleTimerRef = useRef(null);
  const idleExpressionQueueRef = useRef([]);
  const lastIdleExpressionRef = useRef(null);
  const pointerInsideRef = useRef(false);
  const maskId = `botgo-eyes-${useId().replace(/:/g, '')}`;
  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [animation, setAnimation] = useState({ expression: null, run: 0 });

  const triggerExpression = useCallback((nextExpression) => {
    if (
      !animated
      || prefersReducedMotion
      || !isVisible
      || document.visibilityState === 'hidden'
      || !VALID_EXPRESSIONS.has(nextExpression)
    ) {
      return;
    }

    window.clearTimeout(endTimerRef.current);
    setAnimation((current) => ({ expression: nextExpression, run: current.run + 1 }));
    endTimerRef.current = window.setTimeout(() => {
      setAnimation((current) => ({ ...current, expression: null }));
    }, EXPRESSION_DURATIONS[nextExpression] + 80);
  }, [animated, isVisible, prefersReducedMotion]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncPreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
      if (mediaQuery.matches) {
        window.clearTimeout(endTimerRef.current);
        setAnimation((current) => ({ ...current, expression: null }));
      }
    };

    syncPreference();
    mediaQuery.addEventListener?.('change', syncPreference);
    return () => mediaQuery.removeEventListener?.('change', syncPreference);
  }, []);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!expression) return;
    triggerExpression(expression);
  }, [expression, triggerExpression]);

  useEffect(() => {
    window.clearTimeout(idleTimerRef.current);
    if (!animated || !enableIdleMotion || prefersReducedMotion || !isVisible) return undefined;

    const scheduleIdleExpression = () => {
      idleTimerRef.current = window.setTimeout(() => {
        if (document.visibilityState === 'visible' && !pointerInsideRef.current) {
          if (idleExpressionQueueRef.current.length === 0) {
            idleExpressionQueueRef.current = createIdleExpressionQueue(lastIdleExpressionRef.current);
          }

          const nextExpression = idleExpressionQueueRef.current.shift();
          lastIdleExpressionRef.current = nextExpression;
          triggerExpression(nextExpression);
        }
        scheduleIdleExpression();
      }, 5000 + Math.random() * 3000);
    };

    const handleVisibilityChange = () => {
      window.clearTimeout(idleTimerRef.current);
      if (document.visibilityState === 'visible') scheduleIdleExpression();
      else {
        window.clearTimeout(endTimerRef.current);
        setAnimation((current) => ({ ...current, expression: null }));
      }
    };

    scheduleIdleExpression();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearTimeout(idleTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [animated, enableIdleMotion, isVisible, prefersReducedMotion, triggerExpression]);

  useEffect(() => () => {
    window.clearTimeout(endTimerRef.current);
    window.clearTimeout(idleTimerRef.current);
  }, []);

  const activeExpression = animation.expression;
  const duration = activeExpression ? EXPRESSION_DURATIONS[activeExpression] : 0;
  const titleId = `${maskId}-title`;

  const handlePointerEnter = () => {
    if (pointerInsideRef.current) return;
    pointerInsideRef.current = true;
    triggerExpression(hoverExpression);
  };

  const handlePointerLeave = () => {
    pointerInsideRef.current = false;
  };

  return (
    <span
      ref={rootRef}
      className={`${styles.root} ${className}`}
      data-animated-brand-symbol="true"
      data-brand-expression={activeExpression || 'idle'}
      data-reduced-motion={prefersReducedMotion ? 'true' : 'false'}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      style={{ '--botgo-symbol-color': color }}
    >
      <svg
        key={animation.run}
        className={styles.mark}
        data-expression={activeExpression || 'idle'}
        data-playing={activeExpression ? 'true' : 'false'}
        viewBox={lockup ? `0 0 ${LOCKUP_WIDTH} 40` : '0 0 100 100'}
        width={lockup ? size * LOCKUP_WIDTH / 40 : size}
        height={size}
        role={decorative ? 'presentation' : 'img'}
        aria-hidden={decorative ? 'true' : undefined}
        aria-labelledby={decorative ? undefined : titleId}
        focusable="false"
        style={{ '--botgo-motion-duration': `${duration}ms` }}
      >
        {decorative ? null : <title id={titleId}>{alt}</title>}
        <g transform={lockup ? LOCKUP_SYMBOL_TRANSFORM : compact ? COMPACT_SYMBOL_TRANSFORM : SYMBOL_TRANSFORM}>
        <defs>
          <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="100">
            <rect width="100" height="100" fill="#fff" />
            <BaseEyes />
            <AlternateEyes expression={activeExpression} />
          </mask>
        </defs>
        <circle data-brand-symbol="true" cx="50" cy="50" r="38" fill="currentColor" mask={`url(#${maskId})`} />
        </g>
        {lockup ? <path data-brand-wordmark="true" transform={`translate(${WORDMARK_OFFSET_X} 0)`} d={WORDMARK_PATH} fill="currentColor" /> : null}
      </svg>
    </span>
  );
}
